import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { isDarkColor, resolveSolidColor } from '../utils/themeUtils';

export { isDarkColor, resolveSolidColor };

const DEFAULT_FAVICON = '/favicon.svg';

export const updateFavicon = (iconUrl) => {
  if (typeof document === 'undefined') return;
  const targetUrl = iconUrl || DEFAULT_FAVICON;
  const rels = ['icon', 'shortcut icon', 'apple-touch-icon'];
  rels.forEach(rel => {
    let link = document.querySelector(`link[rel="${rel}"]`);
    if (!link) {
      link = document.createElement('link');
      link.rel = rel;
      document.head.appendChild(link);
    }
    link.href = targetUrl;
    if (targetUrl.endsWith('.svg') || targetUrl.startsWith('data:image/svg+xml')) {
      link.type = 'image/svg+xml';
    } else if (targetUrl.endsWith('.png')) {
      link.type = 'image/png';
    } else if (targetUrl.endsWith('.ico')) {
      link.type = 'image/x-icon';
    }
  });
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialLoadDone = useRef(false);

  const refreshTheme = useCallback(async (bypassCache = false) => {
    // Only set loading to true on initial mount; background refreshes should never unmount the app
    if (!initialLoadDone.current) {
      setLoading(true);
    }
    const isDev = window.location.port === '3000';
    const API_BASE = isDev ? `http://${window.location.hostname}:8000` : '';

    try {
      const cbQuery = bypassCache ? `?_cb=${Date.now()}` : '';
      const headers = {
        'X-Tenant-Domain': window.location.hostname
      };
      if (bypassCache) {
        headers['X-Cache-Buster'] = 'true';
      }
      const res = await fetch(`${API_BASE}/api/v1/tenant/config${cbQuery}`, {
        headers
      });
      if (!res.ok) throw new Error("HTTP error while fetching tenant config");
      const data = await res.json();
      
      const base = API_BASE ? API_BASE.replace(/\/+$/, '') : '';
      const logo_url = data.logo_url && data.logo_url.includes('/uploads/')
        ? `${base}${data.logo_url.substring(data.logo_url.indexOf('/uploads/'))}`
        : (data.logo_url || '');
      const site_icon_url = data.site_icon_url && data.site_icon_url.includes('/uploads/')
        ? `${base}${data.site_icon_url.substring(data.site_icon_url.indexOf('/uploads/'))}`
        : (data.site_icon_url || '');
      const background_url = data.background_url && data.background_url.includes('/uploads/')
        ? `${base}${data.background_url.substring(data.background_url.indexOf('/uploads/'))}`
        : (data.background_url || '');
      
      const resolvedData = {
        ...data,
        logo_url,
        site_icon_url,
        background_url,
        logo_offset_y: data.logo_offset_y !== undefined && data.logo_offset_y !== null ? data.logo_offset_y : 0,
        logo_offset_x: data.logo_offset_x !== undefined && data.logo_offset_x !== null ? data.logo_offset_x : 0,
        logo_height: data.logo_height !== undefined && data.logo_height !== null ? data.logo_height : 40,
        banner_height: data.banner_height !== undefined && data.banner_height !== null ? data.banner_height : 70,
        menu_color: data.menu_color || null,
        disabled_color: data.disabled_color || '#000000',
        toast_bg_color: data.toast_bg_color || null,
        toast_text_color: data.toast_text_color || null,
        button_primary_text_color: data.button_primary_text_color || null,
        button_secondary_bg: data.button_secondary_bg || null,
        button_secondary_text: data.button_secondary_text || null,
        button_secondary_border: data.button_secondary_border || null,
        table_header_bg: data.table_header_bg || null,
        table_header_text: data.table_header_text || null,
        table_row_selected: data.table_row_selected || null,
        modal_bg_color: data.modal_bg_color || null,
        modal_border_color: data.modal_border_color || null,
        input_border_color: data.input_border_color || null,
        input_text_color: data.input_text_color || null,
        border_color: data.border_color || null,
        badge_bg_color: data.badge_bg_color || null,
        badge_text_color: data.badge_text_color || null,
        font_family: data.font_family || null,
        border_radius: data.border_radius || 'standard',
        share_payoff_line: data.share_payoff_line || "Official Certified Trophy Card",
        mobile_styles: data.mobile_styles || null,
        allow_public_signup: data.allow_public_signup !== undefined ? data.allow_public_signup : false,
        environment: data.environment || 'development'
      };
      
      setTenantConfig(resolvedData);
      document.title = resolvedData.brand_name || 'TrophyCoin';
      updateFavicon(resolvedData.site_icon_url || resolvedData.logo_url);
      
      // Dynamically resolve theme light/dark setting based on panel color luminance
      const panelBg = data.panel_color || (data.brand_name === "TrophyCoin" || data.brand_name === "TrophyCoin Sandbox" || !data.id ? '#f8f9fa' : '#161b22');
      const isLight = !isDarkColor(panelBg);
      
      const defaultMenuBg = isLight ? '#f8f9fa' : '#161b22';
      const menuBg = data.menu_color || defaultMenuBg;
      const isMenuDark = isDarkColor(menuBg);
      const menuText = isMenuDark ? '#f8fafc' : (resolveSolidColor(data.text_main_color, isLight ? '#111827' : '#c9d1d9'));
      const menuTextMute = isMenuDark ? '#94a3b8' : (resolveSolidColor(data.text_mute_color, isLight ? '#4b5563' : '#8b949e'));

      const defaultDisabledColor = '#000000';
      const disabledBg = data.disabled_color || defaultDisabledColor;
      const solidDisabled = resolveSolidColor(data.disabled_color, defaultDisabledColor);
      const isDisDark = isDarkColor(disabledBg);
      const disabledText = isDisDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.4)';

      const headerBg = data.header_color || data.panel_color || (isLight ? '#f8f9fa' : '#161b22');
      const isHeaderDark = isDarkColor(headerBg);
      const headerText = isHeaderDark ? '#F5F2EB' : (resolveSolidColor(data.text_main_color, isLight ? '#111827' : '#c9d1d9'));
      
      const defaultInputBg = isLight ? '#ffffff' : '#0d1117';
      const inputBg = data.input_bg_color || defaultInputBg;
      const defaultInputText = isLight ? '#111827' : '#c9d1d9';
      const inputText = data.input_text_color ? resolveSolidColor(data.input_text_color, defaultInputText) : (isDarkColor(inputBg) ? '#ffffff' : (resolveSolidColor(data.text_main_color, defaultInputText)));
      const defaultInputBorder = isLight ? '#d1d5db' : '#30363d';
      const inputBorder = data.input_border_color ? resolveSolidColor(data.input_border_color, defaultInputBorder) : defaultInputBorder;

      // Pop-Down Toast System Messages
      const defaultToastBg = isLight ? 'rgba(255, 255, 255, 0.96)' : 'rgba(15, 23, 42, 0.95)';
      const toastBg = data.toast_bg_color || defaultToastBg;
      const isToastDark = isDarkColor(toastBg);
      const defaultToastText = isToastDark ? '#ffffff' : '#0f172a';
      const toastText = data.toast_text_color ? resolveSolidColor(data.toast_text_color, defaultToastText) : defaultToastText;
      const toastBorder = isToastDark ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.12)';

      // Always resolve valid solid colors for properties that require <color> (text, border, shadow)
      const solidPrimary = resolveSolidColor(data.primary_color, isLight ? '#bdb2ff' : '#58a6ff');
      const solidAccent = resolveSolidColor(data.accent_color, isLight ? '#a0c4ff' : '#1f6feb');
      const solidTextMain = resolveSolidColor(data.text_main_color, isLight ? '#111827' : '#c9d1d9');
      const solidTextMute = resolveSolidColor(data.text_mute_color, isLight ? '#4b5563' : '#8b949e');
      
      // Global Borders & Dividers
      const defaultBorderColor = isLight ? '#e5e7eb' : '#30363d';
      const borderColor = data.border_color ? resolveSolidColor(data.border_color, defaultBorderColor) : defaultBorderColor;

      // Button Primary & Secondary Styling
      const defaultPrimaryTextColor = isDarkColor(solidPrimary) ? '#ffffff' : '#0f172a';
      const buttonPrimaryText = data.button_primary_text_color ? resolveSolidColor(data.button_primary_text_color, defaultPrimaryTextColor) : defaultPrimaryTextColor;
      
      const defaultSecondaryBg = isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.05)';
      const buttonSecondaryBg = data.button_secondary_bg || defaultSecondaryBg;
      const defaultSecondaryText = isLight ? '#334155' : '#e2e8f0';
      const buttonSecondaryText = data.button_secondary_text ? resolveSolidColor(data.button_secondary_text, defaultSecondaryText) : defaultSecondaryText;
      const defaultSecondaryBorder = isLight ? '#cbd5e1' : '#30363d';
      const buttonSecondaryBorder = data.button_secondary_border ? resolveSolidColor(data.button_secondary_border, defaultSecondaryBorder) : defaultSecondaryBorder;

      // Tables & Lists Styling
      const defaultTableHeaderBg = isLight ? 'rgba(0, 0, 0, 0.02)' : 'rgba(255, 255, 255, 0.02)';
      const tableHeaderBg = data.table_header_bg || defaultTableHeaderBg;
      const defaultTableHeaderText = isLight ? '#475569' : '#8b949e';
      const tableHeaderText = data.table_header_text ? resolveSolidColor(data.table_header_text, defaultTableHeaderText) : defaultTableHeaderText;
      const defaultRowSelected = isLight ? 'rgba(56, 139, 253, 0.10)' : 'rgba(56, 139, 253, 0.16)';
      const tableRowSelected = data.table_row_selected || defaultRowSelected;

      // Modal Dialogs & Panels
      const defaultModalBg = isLight ? (data.card_color || '#ffffff') : (data.card_color || '#161b22');
      const modalBg = data.modal_bg_color || defaultModalBg;
      const defaultModalBorder = borderColor;
      const modalBorder = data.modal_border_color ? resolveSolidColor(data.modal_border_color, defaultModalBorder) : defaultModalBorder;
      const isModalLight = !isDarkColor(modalBg);
      const isCardLight = !isDarkColor(data.card_color || (isLight ? '#ffffff' : '#161b22'));
      const isContentLight = isLight || isCardLight;

      // Status Badges & Pills
      const defaultBadgeBg = isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.08)';
      const badgeBg = data.badge_bg_color || defaultBadgeBg;
      const defaultBadgeText = isLight ? '#475569' : '#cbd5e1';
      const badgeText = data.badge_text_color ? resolveSolidColor(data.badge_text_color, defaultBadgeText) : defaultBadgeText;

      // Typography Font Family
      const defaultFontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
      const fontFamily = data.font_family || defaultFontFamily;
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.fontFamily = fontFamily;
      }

      // Border Radius / Corner Rounding
      let radiusSm = '4px';
      let radiusMd = '8px';
      let radiusLg = '12px';
      if (data.border_radius === 'sharp') {
        radiusSm = '0px';
        radiusMd = '0px';
        radiusLg = '0px';
      } else if (data.border_radius === 'smooth') {
        radiusSm = '6px';
        radiusMd = '12px';
        radiusLg = '16px';
      } else if (data.border_radius === 'rounded') {
        radiusSm = '8px';
        radiusMd = '16px';
        radiusLg = '24px';
      }

      const themeVars = isLight ? {
        '--bg-color': '#ffffff',
        '--panel-bg': panelBg,
        '--panel-color': panelBg,
        '--menu-bg': menuBg,
        '--menu-color': menuBg,
        '--menu-text': menuText,
        '--menu-text-mute': menuTextMute,
        '--border-color': borderColor,
        '--text-main': solidTextMain,
        '--text-mute': solidTextMute,
        '--primary-color': solidPrimary,
        '--primary-gradient': data.primary_color || solidPrimary,
        '--primary-bg': data.primary_color || solidPrimary,
        '--button-primary-text': buttonPrimaryText,
        '--button-secondary-bg': buttonSecondaryBg,
        '--button-secondary-text': buttonSecondaryText,
        '--button-secondary-border': buttonSecondaryBorder,
        '--table-header-bg': tableHeaderBg,
        '--table-header-text': tableHeaderText,
        '--table-row-selected': tableRowSelected,
        '--modal-bg': modalBg,
        '--modal-border': modalBorder,
        '--badge-bg': badgeBg,
        '--badge-text': badgeText,
        '--font-family': fontFamily,
        '--radius-sm': radiusSm,
        '--radius-md': radiusMd,
        '--radius-lg': radiusLg,
        '--accent-color': solidAccent,
        '--accent-gradient': data.accent_color || solidAccent,
        '--accent-bg': data.accent_color || solidAccent,
        '--disabled-color': solidDisabled,
        '--disabled-bg': disabledBg,
        '--disabled-text': disabledText,
        '--card-shadow': '0 8px 32px rgba(31, 38, 135, 0.08)',
        '--input-bg': inputBg,
        '--input-text': inputText,
        '--input-border': inputBorder,
        '--card-bg': data.card_color || '#ffffff',
        '--header-bg': headerBg,
        '--header-text': headerText,
        '--header-height': `${resolvedData.banner_height}px`,
        '--toast-bg': toastBg,
        '--toast-text': toastText,
        '--toast-border': toastBorder,
        '--warning-bg': isContentLight ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.18)',
        '--warning-border': isContentLight ? 'rgba(217, 119, 6, 0.35)' : 'rgba(245, 158, 11, 0.35)',
        '--warning-text': isContentLight ? '#92400e' : '#fbbf24',
        '--error-bg': isContentLight ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 68, 68, 0.18)',
        '--error-border': isContentLight ? 'rgba(220, 38, 38, 0.30)' : 'rgba(239, 68, 68, 0.35)',
        '--error-text': isContentLight ? '#b91c1c' : '#f87171',
        '--success-bg': isContentLight ? 'rgba(34, 197, 94, 0.10)' : 'rgba(34, 197, 94, 0.18)',
        '--success-border': isContentLight ? 'rgba(22, 163, 74, 0.30)' : 'rgba(34, 197, 94, 0.35)',
        '--success-text': isContentLight ? '#15803d' : '#4ade80',
        '--info-bg': isContentLight ? 'rgba(56, 189, 248, 0.10)' : 'rgba(56, 189, 248, 0.18)',
        '--info-border': isContentLight ? 'rgba(2, 132, 199, 0.30)' : 'rgba(56, 189, 248, 0.35)',
        '--info-text': isContentLight ? '#0369a1' : '#38bdf8',
        '--modal-warning-bg': isModalLight ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.18)',
        '--modal-warning-border': isModalLight ? 'rgba(217, 119, 6, 0.35)' : 'rgba(245, 158, 11, 0.35)',
        '--modal-warning-text': isModalLight ? '#92400e' : '#fbbf24'
      } : {
        '--bg-color': '#0d1117',
        '--panel-bg': panelBg,
        '--panel-color': panelBg,
        '--menu-bg': menuBg,
        '--menu-color': menuBg,
        '--menu-text': menuText,
        '--menu-text-mute': menuTextMute,
        '--border-color': borderColor,
        '--text-main': solidTextMain,
        '--text-mute': solidTextMute,
        '--primary-color': solidPrimary,
        '--primary-gradient': data.primary_color || solidPrimary,
        '--primary-bg': data.primary_color || solidPrimary,
        '--button-primary-text': buttonPrimaryText,
        '--button-secondary-bg': buttonSecondaryBg,
        '--button-secondary-text': buttonSecondaryText,
        '--button-secondary-border': buttonSecondaryBorder,
        '--table-header-bg': tableHeaderBg,
        '--table-header-text': tableHeaderText,
        '--table-row-selected': tableRowSelected,
        '--modal-bg': modalBg,
        '--modal-border': modalBorder,
        '--badge-bg': badgeBg,
        '--badge-text': badgeText,
        '--font-family': fontFamily,
        '--radius-sm': radiusSm,
        '--radius-md': radiusMd,
        '--radius-lg': radiusLg,
        '--accent-color': solidAccent,
        '--accent-gradient': data.accent_color || solidAccent,
        '--accent-bg': data.accent_color || solidAccent,
        '--disabled-color': solidDisabled,
        '--disabled-bg': disabledBg,
        '--disabled-text': disabledText,
        '--card-shadow': '0 8px 32px rgba(0, 0, 0, 0.4)',
        '--input-bg': inputBg,
        '--input-text': inputText,
        '--input-border': inputBorder,
        '--card-bg': data.card_color || '#161b22',
        '--header-bg': headerBg,
        '--header-text': headerText,
        '--header-height': `${resolvedData.banner_height}px`,
        '--toast-bg': toastBg,
        '--toast-text': toastText,
        '--toast-border': toastBorder,
        '--warning-bg': isContentLight ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.18)',
        '--warning-border': isContentLight ? 'rgba(217, 119, 6, 0.35)' : 'rgba(245, 158, 11, 0.35)',
        '--warning-text': isContentLight ? '#92400e' : '#fbbf24',
        '--error-bg': isContentLight ? 'rgba(239, 68, 68, 0.10)' : 'rgba(239, 68, 68, 0.18)',
        '--error-border': isContentLight ? 'rgba(220, 38, 38, 0.30)' : 'rgba(239, 68, 68, 0.35)',
        '--error-text': isContentLight ? '#b91c1c' : '#f87171',
        '--success-bg': isContentLight ? 'rgba(34, 197, 94, 0.10)' : 'rgba(34, 197, 94, 0.18)',
        '--success-border': isContentLight ? 'rgba(22, 163, 74, 0.30)' : 'rgba(34, 197, 94, 0.35)',
        '--success-text': isContentLight ? '#15803d' : '#4ade80',
        '--info-bg': isContentLight ? 'rgba(56, 189, 248, 0.10)' : 'rgba(56, 189, 248, 0.18)',
        '--info-border': isContentLight ? 'rgba(2, 132, 199, 0.30)' : 'rgba(56, 189, 248, 0.35)',
        '--info-text': isContentLight ? '#0369a1' : '#38bdf8',
        '--modal-warning-bg': isModalLight ? 'rgba(245, 158, 11, 0.12)' : 'rgba(245, 158, 11, 0.18)',
        '--modal-warning-border': isModalLight ? 'rgba(217, 119, 6, 0.35)' : 'rgba(245, 158, 11, 0.35)',
        '--modal-warning-text': isModalLight ? '#92400e' : '#fbbf24'
      };

      Object.entries(themeVars).forEach(([key, val]) => {
        document.documentElement.style.setProperty(key, val);
      });
    } catch (err) {
      console.warn("[ThemeProvider] Failed fetching tenant config, defaulting to core TrophyCoin.", err);
      // Fallback variables matching core TrophyCoin (pastel + light mode)
      const fallback = {
        brand_name: "TrophyCoin",
        primary_color: "#bdb2ff",
        accent_color: "#a0c4ff",
        logo_url: "",
        site_icon_url: "",
        background_url: "",
        logo_offset_y: 0,
        logo_offset_x: 0,
        logo_height: 40,
        banner_height: 70,
        card_color: null,
        panel_color: null,
        menu_color: null,
        disabled_color: "#000000",
        header_color: null,
        share_payoff_line: "Official Certified Trophy Card",
        allow_public_signup: false,
        environment: 'development'
      };
      setTenantConfig(fallback);
      document.title = fallback.brand_name || 'TrophyCoin';
      updateFavicon(fallback.site_icon_url || fallback.logo_url);
      
      const fallbackVars = {
        '--bg-color': '#ffffff',
        '--panel-bg': '#f8f9fa',
        '--panel-color': '#f8f9fa',
        '--menu-bg': '#f8f9fa',
        '--menu-color': '#f8f9fa',
        '--menu-text': '#111827',
        '--menu-text-mute': '#4b5563',
        '--border-color': '#e5e7eb',
        '--text-main': '#111827',
        '--text-mute': '#4b5563',
        '--primary-color': fallback.primary_color,
        '--primary-gradient': fallback.primary_color,
        '--primary-bg': fallback.primary_color,
        '--accent-color': fallback.accent_color,
        '--accent-gradient': fallback.accent_color,
        '--accent-bg': fallback.accent_color,
        '--disabled-color': '#000000',
        '--disabled-bg': '#000000',
        '--disabled-text': 'rgba(255, 255, 255, 0.45)',
        '--card-shadow': '0 8px 32px rgba(31, 38, 135, 0.08)',
        '--input-bg': '#ffffff',
        '--input-text': '#111827',
        '--input-border': '#d1d5db',
        '--card-bg': '#ffffff',
        '--header-bg': '#f8f9fa',
        '--header-text': '#111827',
        '--toast-bg': 'rgba(255, 255, 255, 0.96)',
        '--toast-text': '#0f172a',
        '--toast-border': 'rgba(0, 0, 0, 0.12)',
        '--button-primary-text': '#0f172a',
        '--button-secondary-bg': 'rgba(0, 0, 0, 0.03)',
        '--button-secondary-text': '#334155',
        '--button-secondary-border': '#cbd5e1',
        '--table-header-bg': 'rgba(0, 0, 0, 0.02)',
        '--table-header-text': '#475569',
        '--table-row-selected': 'rgba(56, 139, 253, 0.10)',
        '--modal-bg': '#ffffff',
        '--modal-border': '#e5e7eb',
        '--badge-bg': 'rgba(0, 0, 0, 0.06)',
        '--badge-text': '#475569',
        '--font-family': '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        '--radius-sm': '6px',
        '--radius-md': '10px',
        '--radius-lg': '14px',
        '--warning-bg': 'rgba(245, 158, 11, 0.12)',
        '--warning-border': 'rgba(217, 119, 6, 0.35)',
        '--warning-text': '#92400e',
        '--error-bg': 'rgba(239, 68, 68, 0.10)',
        '--error-border': 'rgba(220, 38, 38, 0.30)',
        '--error-text': '#b91c1c',
        '--success-bg': 'rgba(34, 197, 94, 0.10)',
        '--success-border': 'rgba(22, 163, 74, 0.30)',
        '--success-text': '#15803d',
        '--info-bg': 'rgba(56, 189, 248, 0.10)',
        '--info-border': 'rgba(2, 132, 199, 0.30)',
        '--info-text': '#0369a1'
      };

      Object.entries(fallbackVars).forEach(([key, val]) => {
        document.documentElement.style.setProperty(key, val);
      });
    } finally {
      initialLoadDone.current = true;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshTheme();
  }, [refreshTheme]);

  return (
    <ThemeContext.Provider value={{ tenantConfig, loading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
